import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArrowLeft, Pencil, Info, Loader2 } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { FormField } from '../components/FormField';
import { formatHelpText } from '../config/helptext-config';
import { packageFormHelpText } from '../config/helptext-config';
import { S3JsonDropdown } from '../components/S3JsonDropdown';

import { Package } from '../types';
import SkillsSelect from '../agents/SkillsSelect';
import AgentSelect from './AgentsSelect';
import { AccessControlSection } from '../components/AccessControlSection';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import AccessDisplay from '../components/AccessDisplay';
import OwnerSelection from '../components/OwnerSelection';
import ProductSelect from './ProductSelect';
import { Checkbox } from "../components/ui/Checkbox";
import { ADMIN_SERVICE_USER } from '../config/api-config';
import { ExecutionRecordsDisplay } from '../components/ExecutionRecordsDisplay';

export interface PackageFormRef {
    validateForm: () => Promise<boolean>;
    getErrors: () => Record<string, string>;
}

interface AccessControl {
  editAccessType: string;
  editAllowedUsers: string[];
  viewAccessType: string;
  viewAllowedUsers: string[];
}

interface PackageFormProps {
    package: Package;
    originalPackage?: Package | null;
    onPackageChange: (pkg: Package) => void;
    onSubmit: () => Promise<void>;
    onCancel: () => void;
    isEditing?: boolean;
    readOnly?: boolean;
    onEditClick?: () => void;
    onDiscardChanges?: () => void;
    hasUnsavedChanges?: boolean;
    setIsReadOnly: (value: boolean) => void;
    tagSuggestions?: string[];
    existingPackages?: Package[];
    isSaving?: boolean;
  }

// View Mode Field Component
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

interface UserAPIResponse {
  users: {
    userId: string;
    email: string;
    isAdmin: boolean;
    isActive: boolean;
  }[];
}

const SaveOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Saving Package... Please wait</span>
    </div>
  </div>
);

const PackageForm = forwardRef<PackageFormRef, PackageFormProps>((props, ref) => {
  const {
    package: pkg,
    onPackageChange,
    onSubmit,
    onCancel,
    isEditing = false,
    readOnly = false,
    onEditClick,
    onDiscardChanges,
    hasUnsavedChanges = false,
    tagSuggestions = [],
    existingPackages
  } = props;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { user } = useAuth();
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const { getUsers } = useUsersApi();
  const tagManagementRef = useRef<TagManagementRef>(null);
  const [isCorePackage, setIsCorePackage] = useState(pkg.isCore || false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');

  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const usersData = await getUsers();
          const userData = usersData.find(u => u.userId === user.sub);
          setIsCurrentUserAdmin(userData?.isAdmin || false);
          
          // Find the admin service account ID
          const adminServiceAccount = usersData.find(u => 
            u.email === ADMIN_SERVICE_USER
          );
          
          if (adminServiceAccount) {
            setAdminServiceUserId(adminServiceAccount.userId);
          } else {
            console.warn(`Admin service account ${ADMIN_SERVICE_USER} not found in users list`);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsCurrentUserAdmin(false);
        }
      }
    };

    fetchAdminStatus();
  }, [user?.sub]);

  useEffect(() => {
    // When isCorePackage is enabled, set default access control to "Owner Only"
    // and set owner to admin service account
    if (isCorePackage) {
      onPackageChange({
        ...pkg,
        isCore: true,
        ownerId: adminServiceUserId || pkg.ownerId,
        accessControl: {
          viewAccessType: 'Owner Only',
          viewAllowedUsers: [],
          editAccessType: 'Owner Only',
          editAllowedUsers: []
        }
      });
    } else {
      onPackageChange({
        ...pkg,
        isCore: false
      });
    }
  }, [isCorePackage, adminServiceUserId]);

  const hasEditAccess = (accessControl: AccessControl) => {
    const currentUserId = user?.sub;
    const isOwner = pkg.ownerId === currentUserId;
    
    if (!currentUserId) return false;
    
    // Admins always have edit access
    if (isCurrentUserAdmin) return true;
    
    // Owners always have edit access to their own packages
    if (isOwner) return true;
    
    // For non-admin, non-owner users, check access control settings
    const { editAccessType, editAllowedUsers } = accessControl;
    
    switch (editAccessType) {
      case 'All Users':
        return true;
      case 'Allowed Users':
        return editAllowedUsers.includes(currentUserId);
      case 'Owner Only':
        return false;
      default:
        return false;
    }
  };

  const checkNameExists = (name: string): boolean => {
    return existingPackages?.some(existingPackage => 
      existingPackage.packageName.toLowerCase() === name.toLowerCase() && 
      existingPackage.packageId !== pkg.packageId
    ) ?? false;
  };

  const validatePackageName = async (name: string): Promise<{ isValid: boolean; error: string }> => {
    if (!name.trim()) {
      return { isValid: false, error: 'Package Name is required' };
    }
  
    if (name.length > 63) {
      return { isValid: false, error: 'Package Name must be less than 64 characters' };
    }
  
    // Check for disallowed characters
    if (!/^[a-zA-Z0-9_\s-]+$/.test(name)) {
      return { 
        isValid: false, 
        error: 'Package Name can only contain letters, numbers, spaces, underscores, and hyphens'
      };
    }
  
    // Check for uniqueness
    if (checkNameExists(name)) {
      return {
        isValid: false,
        error: 'A Package with this name already exists. Please choose a different name.'
      };
    }
  
    return { isValid: true, error: '' };
  };

  const handleCorePackageChange = (checked: boolean) => {
    if (readOnly) return;
    setIsCorePackage(checked);
  };

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const newErrors: Record<string, string> = {};
  
      const nameValidation = await validatePackageName(pkg.packageName);
      if (!nameValidation.isValid) {
        newErrors.packageName = nameValidation.error;
      }
  
      if (!pkg.packageProductName.trim()) {
        newErrors.packageProductName = 'Package Product Name is required';
      }
      if (!pkg.packageDescription.trim()) {
        newErrors.packageDescription = 'Package Description is required';
      }
      if (!pkg.packageWorkflow.trim()) {
        newErrors.packageWorkflow = 'Package Workflow is required';
      }
      if (!pkg.packageActiveSkills?.length) {
        newErrors.packageActiveSkills = 'At least one Active Skill is required';
      }
      if (!pkg.packageHelperAgents?.length) {
        newErrors.packageHelperAgents = 'At least one Helper Agent is required';
      }
  
      // Add access control validation only if not a core package
      if (!isCorePackage) {
        if (!pkg.accessControl.viewAccessType) {
          newErrors.viewAccessType = 'View Access Type is required';
        }

        if (!pkg.accessControl.editAccessType) {
          newErrors.editAccessType = 'Edit Access Type is required';
        }
      }
      
      setErrors(newErrors);
      
      // If validation passed, process any pending tags
      if (Object.keys(newErrors).length === 0) {
        // Process pending tags if any
        if (tagManagementRef.current) {
          await tagManagementRef.current.processPendingTags();
        }
      }
      
      return Object.keys(newErrors).length === 0;
    },
    getErrors: () => errors
  }));

  const AccessControlViewSection = () => {
    const [ownerDetails, setOwnerDetails] = useState({ email: '', name: '' });
    
    useEffect(() => {
      const fetchOwnerDetails = async () => {
        try {
          const usersData = await getUsers();
          const owner = usersData.find((user) => user.userId === pkg.ownerId);
          if (owner) {
            setOwnerDetails({
              email: owner.email,
              name: owner.email //TODO: Add name to user table
            });
          }
        } catch (error) {
          console.error('Error fetching owner details:', error);
        }
      };
      
      fetchOwnerDetails();
    }, [pkg.ownerId]);

    // For Core Packages, show a special message
    if (pkg.isCore) {
      return (
        <ViewModeField
          label="Access Control"
          value={
            <div className="flex items-center space-x-2 text-gray-700">
              <Info className="h-5 w-5 text-blue-500" />
              <div>
                <p className="font-medium">Administrator Only</p>
                <p className="text-sm text-gray-600">
                  This Core Package is managed by administrators only.
                </p>
              </div>
            </div>
          }
        />
      );
    }
  
    return (
      <ViewModeField
        label="Access Control"
        value={
      <AccessDisplay
        viewAccessType={pkg.accessControl.viewAccessType}
        editAccessType={pkg.accessControl.editAccessType}
        viewAllowedUsers={pkg.accessControl.viewAccessType === 'Allowed Users' 
          ? pkg.accessControl.viewAllowedUsers 
          : []}
        editAllowedUsers={pkg.accessControl.editAccessType === 'Allowed Users' 
          ? pkg.accessControl.editAllowedUsers 
          : []}
        ownerEmail={ownerDetails.email}
        ownerName={ownerDetails.name}
      />
        }
      />
    );
  };

  const handleInputChange = async (field: keyof Package, value: any) => {
    if (readOnly) return;
    
    onPackageChange({
      ...pkg,
      [field]: value
    });
  
    // Immediate validation for packageName field
    if (field === 'packageName') {
      const validation = await validatePackageName(value);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          packageName: validation.error
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.packageName;
          return newErrors;
        });
      }
    }
  };

  const ListDisplay: React.FC<{ 
    items: any[]; 
    type: 'skill' | 'agent';
  }> = ({ items, type }) => {
    if (items.length === 0) {
      return <div className="text-gray-500 italic">No {type}s configured</div>;
    }
  
    return (
      <div className="space-y-4">
        {items.map((item) => (
          <div key={type === 'skill' ? item.skillId : item.agentId} 
               className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="font-medium text-gray-900">
              {type === 'skill' ? item.skillName : item.agentName}
            </div>
            <p className="text-gray-600 mt-1">
              {type === 'skill' ? item.skillDescription : item.agentDescription}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const renderEditMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl">
      {isEditing ? 'Edit Package' : 'Add New Package'}
    </CardTitle>
    <Button
      variant="outline"
      onClick={onCancel}
      className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600 mt-2 sm:mt-0"
    >
      <ArrowLeft className="mr-2 h-4 w-4" />
      Back to Packages
    </Button>
  </div>
</CardHeader>

      <CardContent className="space-y-8">
        <FormField
          label="Package Name"
          helpText={formatHelpText(packageFormHelpText.packageName)}
          required
          error={errors.packageName}
        >
          <Input
            value={pkg.packageName}
            onChange={(e) => handleInputChange('packageName', e.target.value)}
            placeholder="Enter package name"
            className={errors.packageName ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Package Product Name"
          helpText={formatHelpText(packageFormHelpText.packageProductName)}
          required
          error={errors.packageProductName}
        >
          <ProductSelect
            selectedProduct={pkg.packageProductName}
            onChange={(productName) => handleInputChange('packageProductName', productName)}
            readOnly={readOnly}
          />
        </FormField>

        <FormField
          label="Package Description"
          helpText={formatHelpText(packageFormHelpText.packageDescription)}
          required
          error={errors.packageDescription}
        >
          <Textarea
            value={pkg.packageDescription}
            onChange={(e) => handleInputChange('packageDescription', e.target.value)}
            placeholder="Enter package description"
            rows={4}
            className={errors.packageDescription ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Package Model"
          helpText="Choose the Large Language Model for your package. This will apply to all Agents in the swarm."
          required
          error={errors.packageModel}
        >
          <S3JsonDropdown
            bucketName="agenticgenie-files"
            jsonKey="utilities/agenticgenie-llm-models.json"
            value={pkg.packageModel || 'gpt-4o'}
            onChange={(value) => handleInputChange('packageModel', value)}
            placeholder="Select package model"
          />
        </FormField>

        <FormField
          label="Tags"
          helpText={formatHelpText(packageFormHelpText.packageTags)}
        >
          <TagManagement
            ref={tagManagementRef}
            tags={pkg.packageTags}
            onChange={(newTags) => handleInputChange('packageTags', newTags)}
            readOnly={false}
            suggestions={tagSuggestions}
            className="w-full"
          />
          <p className="text-sm text-muted-foreground mt-1">
            {/* Search existing tags or type to add new ones */}
          </p>
        </FormField>

        <FormField
          label="Package Workflow"
          helpText={formatHelpText(packageFormHelpText.packageWorkflow)}
          required
          error={errors.packageWorkflow}
        >
          <Textarea
            value={pkg.packageWorkflow}
            onChange={(e) => handleInputChange('packageWorkflow', e.target.value)}
            placeholder="Enter package workflow"
            rows={4}
            className={errors.packageWorkflow ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Special Instructions"
          helpText={formatHelpText(packageFormHelpText.packageSpecialInstructions)}
        >
          <Textarea
            value={pkg.packageSpecialInstructions}
            onChange={(e) => handleInputChange('packageSpecialInstructions', e.target.value)}
            placeholder="Enter special instructions"
            rows={3}
          />
        </FormField>

        <FormField
          label="Approval Notes"
          helpText={formatHelpText(packageFormHelpText.packageApprovalNotes)}
        >
          <Textarea
            value={pkg.packageApprovalNotes}
            onChange={(e) => handleInputChange('packageApprovalNotes', e.target.value)}
            placeholder="Enter approval notes"
            rows={3}
          />
        </FormField>

        <FormField
          label="Package Active Skills"
          helpText={formatHelpText(packageFormHelpText.packageActiveSkills)}
          required
          error={errors.packageActiveSkills}
        >
          <SkillsSelect
            selectedSkills={pkg.packageActiveSkills || []}
            onChange={(skills) => handleInputChange('packageActiveSkills', skills)}
            readOnly={readOnly}
          />
        </FormField>

        <FormField
          label="Package Helper Agents"
          helpText={formatHelpText(packageFormHelpText.packageHelperAgents)}
          required
          error={errors.packageHelperAgents}
        >
          <AgentSelect
            selectedAgents={pkg.packageHelperAgents || []}
            onChange={(agents) => handleInputChange('packageHelperAgents', agents)}
            readOnly={readOnly}
          />
        </FormField>

        

        {isCurrentUserAdmin && (
          <div className="flex items-start space-x-2 border p-4 rounded-md bg-gray-50">
            <Checkbox 
              id="core-package" 
              checked={isCorePackage}
              onCheckedChange={handleCorePackageChange}
            />
            <div className="flex flex-col">
              <label 
                htmlFor="core-package" 
                className="text-sm font-medium cursor-pointer"
              >
                Core Package (Admin Only)
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Core packages have fixed access control set to "Owner Only", will be owned by the admin service account, and cannot be modified by regular users.
              </p>
            </div>
          </div>
        )}

        {!isCorePackage && (
          <AccessControlSection<Package>
            entity={pkg}
            entityType="Package"
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {isEditing && isCurrentUserAdmin && !isCorePackage && (
          <OwnerSelection
            currentOwnerId={pkg.ownerId}
            onOwnerChange={(newOwnerId) => handleInputChange('ownerId', newOwnerId)}
            isReadOnly={false}
          />
        )}

        {isEditing && isCurrentUserAdmin && isCorePackage && adminServiceUserId && (
          <div className="border p-4 rounded-md bg-blue-50">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Owner</h3>
            <p className="text-sm text-blue-700">
              This Core Package will be owned by the admin service account: <span className="font-semibold">{ADMIN_SERVICE_USER}</span>
            </p>
          </div>
        )}

<div className="flex justify-end gap-2 pt-4">
  {isEditing && onDiscardChanges && (
    <Button
      variant="outline"
      onClick={onDiscardChanges}
      className="border-gray-300 text-gray-700 hover:bg-gray-50"
      disabled={props.isSaving}
    >
      Cancel Edit
    </Button>
  )}
  <Button
    variant="default"
    onClick={onSubmit}
    className="bg-blue-500 hover:bg-blue-600 text-white"
    disabled={props.isSaving}
  >
    {props.isSaving ? (
      <div className="flex items-center space-x-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Saving...</span>
      </div>
    ) : (
      'Save'
    )}
  </Button>
</div>
      </CardContent>
    </Card>
  );

  const renderViewMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4 pb-2">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
    <CardTitle className="text-2xl break-words max-w-full overflow-hidden text-ellipsis">
      {pkg.packageName}
    </CardTitle>
    <div className="flex gap-2 mt-2 sm:mt-0 flex-shrink-0">
      {onEditClick && hasEditAccess(pkg.accessControl) && (
        <Button
          variant="outline"
          onClick={onEditClick}
          className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </Button>
      )}
      <Button
        variant="outline"
        onClick={onCancel}
        className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Packages
      </Button>
    </div>
  </div>
</CardHeader>

      <CardContent className="space-y-8">
        <ViewModeField 
          label="Package Product Name" 
          value={pkg.packageProductName} 
        />
        
        <ViewModeField 
          label="Description" 
          value={pkg.packageDescription} 
        />

        <ViewModeField 
          label="Package Model" 
          value={pkg.packageModel || 'gpt-4o'} 
        />
        
        <ViewModeField 
          label="Workflow" 
          value={pkg.packageWorkflow} 
        />
        
        <ViewModeField 
          label="Special Instructions" 
          value={pkg.packageSpecialInstructions || 'No special instructions provided'} 
        />
        
        <ViewModeField 
          label="Approval Notes" 
          value={pkg.packageApprovalNotes || 'No approval notes provided'} 
        />

        <ViewModeField 
          label="Active Skills" 
          value={
            <ListDisplay 
              items={pkg.packageActiveSkills || []} 
              type="skill"
            />
          }
        />

        <ViewModeField 
          label="Helper Agents" 
          value={
            <ListDisplay 
              items={pkg.packageHelperAgents || []} 
              type="agent"
            />
          }
        />
        
        <ViewModeField 
          label="Tags" 
          value={
            <TagManagement
              tags={pkg.packageTags}
              onChange={() => {}}
              readOnly={true}
              suggestions={tagSuggestions}
            />
          } 
        />

        {/* Show Access Control section for all packages, with special handling for Core Packages inside the component */}
        <AccessControlViewSection />

        {/* Execution Records Section */}
        <ViewModeField 
          label="Execution Records" 
          value={
            <ExecutionRecordsDisplay 
              packageId={pkg.packageId} 
              packageName={pkg.packageName}
              maxRecords={10} 
              hidePackageName={true}
              hideOwner={true}
            />
          } 
        />

      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4" data-has-unsaved-changes={hasUnsavedChanges}>
      {props.isSaving && <SaveOverlay />}
      {readOnly ? renderViewMode() : renderEditMode()}
    </div>
  );
});

export default PackageForm;